import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
  memo,
} from 'react';
import { ImageProps } from 'next/image';
import { mergeRefs } from 'react-merge-refs';
import { Box } from '@design-system/pc';
import styled, { css } from 'styled-components';
import {
  getImageSrcSet as createImageSrcSet,
  CDNResizeParams,
} from '@frontend-common-sdk/shared';

type BaseLoadImageProps = ImageProps &
  Omit<Partial<CDNResizeParams>, 'url'> & {
    className?: string;
    imgFit?: ImageProps['layout'];
    objectFit?: CSSProperties['objectFit'];
    handleChangeMeta?(args: { width: number; height: number }): void;
    skeletonSize?: { width?: string; height?: string };
    borderRadius?: React.CSSProperties['borderRadius'];
    imageScale?: number;
  };

/**
 * 이미지 리사이징을 위한 fallback 값의 필수 여부를 결정하는 조건부 타입
 *
 * 규칙:
 * - `w` 또는 `h` 중 하나는 반드시 필수
 * - `w`와 `h`가 모두 없으면 `fallbackWidth` 또는 `fallbackHeight` 중 하나는 필수
 *
 * @example
 * ```ts
 * // ✅ w와 h 모두 제공
 * { w: 400, h: 300 }
 *
 * // ✅ w만 제공
 * { w: 400 }
 *
 * // ✅ h만 제공
 * { h: 300 }
 *
 * // ✅ w와 h 모두 없음 → fallbackWidth 또는 fallbackHeight 중 하나 필수
 * { fallbackWidth: 400 }
 * { fallbackHeight: 300 }
 * { fallbackWidth: 400, fallbackHeight: 300 }
 *
 * // ❌ w, h, fallback 모두 없음 → 타입 에러
 * {}
 * ```
 */
type FallbackRequirement =
  | { w: number; fallbackWidth?: number; fallbackHeight?: number }
  | { h: number; fallbackWidth?: number; fallbackHeight?: number }
  | { w?: never; h?: never; fallbackWidth: number; fallbackHeight?: number }
  | { w?: never; h?: never; fallbackHeight: number; fallbackWidth?: number };

export type LoadImagePropsType = BaseLoadImageProps &
  Omit<Partial<CDNResizeParams>, 'url'> &
  FallbackRequirement;

// 상수
const DEFAULT_IMAGE_SCALE = 1;
const DEFAULT_BORDER_RADIUS = 0;
const DEFAULT_IMG_FIT: ImageProps['layout'] = 'fill';

/**
 * 이미지 메타데이터를 추출
 */
const extractImageMeta = (
  imageElement: HTMLImageElement | null,
): { width: number; height: number } | null => {
  if (!imageElement?.naturalWidth || !imageElement?.naturalHeight) {
    return null;
  }

  return {
    width: imageElement.naturalWidth,
    height: imageElement.naturalHeight,
  };
};

/**
 * 이미지가 로드가 완료된 이후에 노출
 * @author 이현우
 */
const LoadImage = React.forwardRef<HTMLImageElement, LoadImagePropsType>(
  function LoadImage(
    {
      src,
      w,
      h,
      c,
      className,
      imgFit = DEFAULT_IMG_FIT,
      objectFit,
      imageScale = DEFAULT_IMAGE_SCALE,
      skeletonSize: _skeletonSize,
      borderRadius = DEFAULT_BORDER_RADIUS,
      fallbackWidth,
      fallbackHeight,
      ...props
    }: LoadImagePropsType,
    ref,
  ) {
    const imageRef = useRef<HTMLImageElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const mergeRef = mergeRefs<HTMLElement>([ref, imageRef]);
    const [imageLoaded, setImageLoaded] = useState<boolean>(false);
    const [isClient, setIsClient] = useState<boolean>(false);

    /**
     * 이미지 메타데이터 변경 핸들러
     */
    const handleChangeMeta = useCallback(() => {
      const meta = extractImageMeta(imageRef.current);
      if (meta) {
        props?.handleChangeMeta?.(meta);
      }
    }, [props]);

    /**
     * 이미지 로드 상태 업데이트
     */
    const updateLoadedState = useCallback(() => {
      setImageLoaded(true);
      handleChangeMeta();
    }, [handleChangeMeta]);

    /**
     * 이미지 에러 핸들러
     */
    const handleImageError: React.ReactEventHandler<HTMLImageElement> =
      useCallback(
        (e) => {
          props?.onError?.(e);
        },
        [props],
      );

    /**
     * 이미 이미 로드된 이미지의 상태 업데이트
     */
    const updateAlreadyLoadedImageState = useCallback(() => {
      if (!imageLoaded && imageRef?.current?.complete) {
        updateLoadedState();
        props?.onLoad?.({
          status: 'alreadyLoaded',
        } as any);
      }
    }, [imageLoaded, props, updateLoadedState]);

    /**
     * 이미지 srcset 생성
     */
    const getImageSrcSetValue = useCallback((): string | undefined => {
      if (!isClient) {
        return undefined;
      }

      const width = w ?? fallbackWidth;
      const height = h ?? fallbackHeight;

      if (width === undefined) {
        return undefined;
      }

      return createImageSrcSet({
        url: src as string,
        w: width,
        h: height,
        c,
      });
    }, [isClient, w, fallbackWidth, h, fallbackHeight, src, c]);

    const imageSrcSet = getImageSrcSetValue();

    /**
     * URL에 쿼리 파라미터를 추가하는 헬퍼 함수
     */
    const addQueryParam = useCallback(
      (url: string, param: string, value: number | undefined): string => {
        if (value === undefined) {
          return url;
        }
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${param}=${value}`;
      },
      [],
    );

    /**
     * Next.js Image 컴포넌트의 ref 수동 처리
     * (next/image 13.0.6 이전 버전은 ref를 지원하지 않음)
     */
    useEffect(() => {
      if (wrapperRef.current) {
        const imgElement = wrapperRef.current.querySelector('img');
        if (imgElement) {
          mergeRef(imgElement);
        }
      }
    }, [mergeRef, src]);

    /**
     * 클라이언트 사이드 마운트 및 이미지 상태 초기화
     */
    useEffect(() => {
      setIsClient(true);
      updateAlreadyLoadedImageState();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isClient || !src) return null;

    /**
     * 일반 img 태그 렌더링
     */
    const renderStandardImage = () => (
      <Box position="relative" width="100%" height="100%">
        <MemorizedStyledImage
          loading="lazy"
          ref={mergeRef}
          src={addQueryParam(src as string, 'w', w)}
          srcSet={imageSrcSet}
          alt={props.alt || ''}
          objectFit={objectFit}
          className={className}
          style={{
            borderRadius: `${borderRadius}px`,
          }}
          onError={handleImageError}
          {...props}
        />
      </Box>
    );

    return renderStandardImage();
  },
);

export default LoadImage;

interface StyledImageProps {
  objectFit?: CSSProperties['objectFit'];
  className?: string;
  style?: React.CSSProperties;
  src?: string;
  alt?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

const StyledImage = styled.img<StyledImageProps>`
  ${({ objectFit }) => css`
    width: 100%;
    height: 100%;
    object-fit: ${objectFit || 'cover'};
  `}
`;

const MemorizedStyledImage = memo(StyledImage);
